import { useEffect, useState } from "react";
import { CalendarPlus, ChevronRight, ImagePlus, MapPin, Plus, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReminderBanner from "@/components/ReminderBanner";

const emptyForm = {
  name: "",
  date: "",
  location: "",
  rings: 2,
  status: "draft",
  weight_class_type: "amateur",
  combine_adult_ages: true,
  description: "",
  image_base64: "",
  registration_url: "",
  calendar_published: false,
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const { user } = useAuth();
  const canManage = ["super_admin", "event_admin"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const load = () => api.get("/events").then((response) => setEvents(response.data));
  useEffect(() => { load(); }, []);

  const uploadImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, image_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const create = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const payload = {
        ...form,
        rings: Number(form.rings),
        image_base64: form.image_base64 || null,
        registration_url: form.registration_url || null,
      };
      await api.post("/events", payload);
      setShowForm(false);
      setForm(emptyForm);
      load();
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  return (
    <div className="content-frame" data-testid="events-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">TURNIER-KALENDER</p>
          <h1>EVENTS</h1>
          <p className="muted">Alle WBC Germany Muay Thai Meisterschaften an einem Ort.</p>
        </div>
        {canManage && <button className="button primary" onClick={() => setShowForm(!showForm)} data-testid="create-event-button"><Plus size={18} /> EVENT ANLEGEN</button>}
      </header>
      <ReminderBanner />
      {showForm && (
        <form className="create-event-form" onSubmit={create} data-testid="create-event-form">
          <b>NEUES EVENT</b>
          {error && <p className="form-error" data-testid="create-event-error">{error}</p>}
          <div className="form-grid">
            <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required data-testid="event-name-input" /></label>
            <label>Datum<input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} required data-testid="event-date-input" /></label>
            <label>Ort<input value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} required data-testid="event-location-input" /></label>
            <label>Ringe<input type="number" min="1" max="12" value={form.rings} onChange={(event) => setForm({ ...form, rings: event.target.value })} required data-testid="event-rings-input" /></label>
          </div>
          <div className="form-grid">
            <label>Turnier-Typ (Gewichtsklassen)
              <select value={form.weight_class_type} onChange={(event) => setForm({ ...form, weight_class_type: event.target.value })} data-testid="event-weight-class-type-select">
                <option value="amateur">Amateur (13 M / 10 W · z. B. -71 kg)</option>
                <option value="pro">Profi (17 M / 18 W · z. B. -72.58 kg)</option>
                <option value="kinder_jugend">Kinder & Jugend (U8–U18)</option>
                <option value="mixed">Gemischt (Hausgala · Regelwerk pro Kampf, Catchweight möglich)</option>
              </select>
            </label>
            <label className="calendar-toggle">
              <input type="checkbox" checked={form.combine_adult_ages} onChange={(event) => setForm({ ...form, combine_adult_ages: event.target.checked })} data-testid="event-combine-adult-ages-toggle" />
              <span>Erwachsene (Ü18) zusammenlegen — Junior-Senior und Senior kämpfen gemeinsam</span>
            </label>
          </div>
          {isSuperAdmin && (
            <div className="calendar-fields" data-testid="calendar-fields">
              <p className="eyebrow">EVENTKALENDER-BEITRAG</p>
              <label>Beschreibung<textarea rows={3} maxLength={500} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Kurzer Werbetext für den Eventkalender (optional, max. 500 Zeichen)" data-testid="event-description-input" /></label>
              <label>Meldelink (URL)<input type="url" value={form.registration_url} onChange={(event) => setForm({ ...form, registration_url: event.target.value })} placeholder="https://..." data-testid="event-registration-url-input" /></label>
              <label className="calendar-image-label">Beitragsbild
                <div className="calendar-image-picker">
                  <input type="file" accept="image/*" onChange={uploadImage} data-testid="event-image-input" />
                  {!form.image_base64 && <span><ImagePlus size={16} /> Bild auswählen</span>}
                </div>
              </label>
              {form.image_base64 && <img className="calendar-upload-preview" src={form.image_base64} alt="Vorschau" data-testid="event-image-preview" />}
              <label className="calendar-toggle">
                <input type="checkbox" checked={form.calendar_published} onChange={(event) => setForm({ ...form, calendar_published: event.target.checked })} data-testid="event-calendar-published-toggle" />
                <span>Im öffentlichen Eventkalender anzeigen</span>
              </label>
            </div>
          )}
          <div className="form-actions">
            <button className="button ghost" type="button" onClick={() => setShowForm(false)} data-testid="cancel-create-event-button">Abbrechen</button>
            <button className="button primary" data-testid="save-event-button">Event speichern</button>
          </div>
        </form>
      )}
      <section className="events-grid">
        {events.length ? (() => {
          const parseLastDay = (dateStr) => {
            if (!dateStr) return null;
            // Alle ISO-Datums in einem String finden (unterstützt "2026-09-05",
            // "2026-09-05 – 2026-09-06", "2026-09-05 to 2026-09-06", "2026-09-05, 2026-09-06").
            const matches = String(dateStr).match(/\d{4}-\d{2}-\d{2}/g) || [];
            if (!matches.length) return null;
            const last = matches[matches.length - 1];
            return new Date(last + "T00:00:00");
          };
          const today = new Date(); today.setHours(0, 0, 0, 0);
          // Karenzzeit: Event gilt erst 2 Tage nach dem letzten Turniertag als „beendet".
          // Beispiel: Event am 05.09. → BEENDET ab 07.09.
          const isPast = (endDate) => {
            if (!endDate) return false;
            const cutoff = new Date(endDate.getTime());
            cutoff.setDate(cutoff.getDate() + 2);
            cutoff.setHours(0, 0, 0, 0);
            return today >= cutoff;
          };
          const upcoming = [], past = [];
          events.forEach((ev) => {
            const d = parseLastDay(ev.date);
            if (isPast(d)) past.push(ev); else upcoming.push(ev);
          });
          upcoming.sort((a, b) => (parseLastDay(a.date)?.getTime() || 0) - (parseLastDay(b.date)?.getTime() || 0));
          past.sort((a, b) => (parseLastDay(b.date)?.getTime() || 0) - (parseLastDay(a.date)?.getTime() || 0));
          const derivedStatus = (event) => {
            const d = parseLastDay(event.date);
            if (isPast(d)) return { label: "BEENDET", cls: "completed" };
            if (event.bracket_state === "published") return { label: "FREIGEGEBEN", cls: "active" };
            if (event.bracket_state === "draft") return { label: "AUSGELOST", cls: "draft" };
            if (event.status === "active") return { label: "LIVE", cls: "active" };
            if (event.status === "completed") return { label: "BEENDET", cls: "completed" };
            return { label: "ENTWURF", cls: "draft" };
          };
          const renderCard = (event, index) => {
            const s = derivedStatus(event);
            return (
              <Link className="event-card" to={`/events/${event.id}`} key={event.id} style={{ animationDelay: `${index * 70}ms` }} data-testid={`event-card-${event.id}`}>
                <div className="event-card-head">
                  <span className={`status-pill ${s.cls}`} data-testid={`event-status-${event.id}`}>{s.label}</span>
                  <Trophy size={20} />
                </div>
                <h2>{event.name}</h2>
                <div className="event-meta">
                  <span><CalendarPlus size={15} />{event.date}</span>
                  <span><MapPin size={15} />{event.location}</span>
                </div>
                <footer>
                  <span>{event.rings} {event.rings === 1 ? "Ring" : "Ringe"} · {event.weight_class_type === "pro" ? "PROFI" : event.weight_class_type === "kinder_jugend" ? "KINDER & JUGEND" : event.weight_class_type === "mixed" ? "GEMISCHT" : "AMATEUR"}{event.calendar_published ? " · KALENDER" : ""}</span>
                  <ChevronRight size={18} />
                </footer>
              </Link>
            );
          };
          return (<>
            <div className="events-section" data-testid="events-upcoming-section" style={{gridColumn:"1 / -1"}}><h3 style={{color:"#f4d33e",letterSpacing:0.6,margin:"0 0 12px",fontSize:14}}>BEVORSTEHENDE · {upcoming.length}</h3></div>
            {upcoming.length ? upcoming.map(renderCard) : <div className="empty-state" style={{gridColumn:"1 / -1"}} data-testid="events-upcoming-empty"><p className="muted">Keine bevorstehenden Events.</p></div>}
            <div className="events-section" data-testid="events-past-section" style={{gridColumn:"1 / -1",marginTop:32}}><h3 style={{color:"#8a8a8a",letterSpacing:0.6,margin:"0 0 12px",fontSize:14}}>GELAUFENE · {past.length}</h3></div>
            {past.length ? past.map(renderCard) : <div className="empty-state" style={{gridColumn:"1 / -1"}} data-testid="events-past-empty"><p className="muted">Keine gelaufenen Events.</p></div>}
          </>);
        })() : (
          <div className="empty-state" data-testid="events-empty-state">
            <CalendarPlus size={32} />
            <h2>NOCH KEIN EVENT</h2>
            <p>{canManage ? "Lege das erste Turnier an, um Kämpfer zu registrieren." : "Es wurden noch keine Turniere angelegt."}</p>
          </div>
        )}
      </section>
    </div>
  );
}
